import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Activity, AlertTriangle, ArrowUpRight, Ban, BrainCircuit, CheckCircle2,
    ChevronDown, ChevronRight, Clock, Cpu, Gauge, Loader2, Mail, Megaphone,
    Play, RefreshCw, Rocket, Search, Send, ShieldAlert, Sparkles, TrendingUp,
    Wrench, Zap,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";


const PRIORITY_STYLE = {
    high: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    medium: "text-neutral-400 bg-neutral-400/10 border-neutral-400/30",
    low: "text-text-secondary bg-white/5 border-white/10",
};

const RISK_STYLE = {
    high: "text-neutral-400",
    medium: "text-neutral-400",
    low: "text-[var(--color-success)]",
};

const BLOCK_META = {
    diagnostics: { icon: Search, title: "Block 1 · AutoDiagnosticsOS",
                    subtitle: "Rules-based health scan across marketplace, plans, endpoints and content." },
    improvement: { icon: Wrench, title: "Block 2 · AutoImprovementOS",
                    subtitle: "Aggregates Fase-10 pending items + LLM architectural suggestions." },
    expansion: { icon: Rocket, title: "Block 3 · AutoExpansionSuggestionsOS",
                  subtitle: "3 strategic new-product ideas grounded in real KPIs." },
    marketing: { icon: Megaphone, title: "Block 4 · AutoMarketingSuggestionsOS",
                  subtitle: "5 concrete marketing plays for the current segment mix." },
    analytics: { icon: TrendingUp, title: "Block 5a · AutoAnalyticsOS",
                  subtitle: "30d vs 13w KPI baseline · anomaly z-score alerts." },
    scaling: { icon: Cpu, title: "Block 5b · AutoScalingAdvisorOS",
                subtitle: "Infra + performance heuristics (indexes, hot paths, cache)." },
    evolution: { icon: BrainCircuit, title: "Block 5c · AutoEvolutionAdvisorOS",
                  subtitle: "4-week execution roadmap synthesised from the whole run." },
};


function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-neutral-400",
        neutral: "text-text-secondary",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}


function PriorityChip({ level, testid }) {
    const cls = PRIORITY_STYLE[String(level || "low").toLowerCase()] || PRIORITY_STYLE.low;
    return (
        <span
            data-testid={testid}
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}
        >
            {level || "low"}
        </span>
    );
}


function SuggestionRow({ suggestion, reportId, onChanged }) {
    const [busy, setBusy] = useState(false);
    const status = suggestion.status || "pending_review";
    const isPending = status === "pending_review";

    const title = suggestion.title || suggestion.name || suggestion.summary
                    || suggestion.interpretation || suggestion.issue_type
                    || suggestion.advice_type || suggestion.hook_copy
                    || suggestion.suggestion_type || suggestion.metric_impacted
                    || suggestion.metric || suggestion.alert_type
                    || "—";
    const description = suggestion.reasoning || suggestion.description
                          || suggestion.summary || suggestion.expected_gain
                          || suggestion.suggested_fix || suggestion.interpretation
                          || "";

    const act = async (kind) => {
        setBusy(true);
        try {
            const path = kind === "ack"
                ? `/v50/reports/${encodeURIComponent(reportId)}/acknowledge/${encodeURIComponent(suggestion.id)}`
                : `/v50/reports/${encodeURIComponent(reportId)}/dismiss/${encodeURIComponent(suggestion.id)}`;
            const body = kind === "dismiss" ? { reason: prompt("Dismiss reason (optional):") || "" } : {};
            await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
            toast.success(kind === "ack" ? "Acknowledged" : "Dismissed");
            onChanged?.();
        } catch (e) {
            toast.error(e.message || "Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <li
            data-testid={`v50-suggestion-${suggestion.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <PriorityChip level={suggestion.priority} testid={`v50-prio-${suggestion.id}`} />
                        {suggestion.risk_level && (
                            <span className={`text-[10px] uppercase tracking-widest ${RISK_STYLE[String(suggestion.risk_level).toLowerCase()] || "text-text-secondary"}`}>
                                risk · {suggestion.risk_level}
                            </span>
                        )}
                        {suggestion.ai_generated && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] flex items-center gap-1">
                                <Sparkles className="h-3 w-3" /> ai
                            </span>
                        )}
                        {status !== "pending_review" && (
                            <span className={`text-[10px] uppercase tracking-widest ${status === "acknowledged" ? "text-[var(--color-success)]" : "text-text-secondary"}`}>
                                · {status}
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-text-main font-medium truncate">{title}</div>
                    {description && (
                        <p className="text-xs text-text-secondary mt-1 line-clamp-3">{description}</p>
                    )}
                    {suggestion.suggested_fix && suggestion.suggested_fix !== description && (
                        <p className="text-[11px] text-text-secondary mt-1 italic">
                            → {suggestion.suggested_fix}
                        </p>
                    )}
                </div>
                {isPending && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            data-testid={`v50-ack-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("ack")}
                            className="text-[10px] rounded-full border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-3 py-1 hover:bg-[var(--color-primary)]/10 disabled:opacity-50 flex items-center gap-1"
                        >
                            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                            Ack
                        </button>
                        <button
                            data-testid={`v50-dismiss-${suggestion.id}`}
                            disabled={busy}
                            onClick={() => act("dismiss")}
                            className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary px-3 py-1 hover:text-text-main disabled:opacity-50 flex items-center gap-1"
                        >
                            <Ban className="h-3 w-3" />
                            Dismiss
                        </button>
                    </div>
                )}
            </div>
        </li>
    );
}


function BlockShell({ blockKey, block, reportId, onChanged, defaultOpen }) {
    const [open, setOpen] = useState(!!defaultOpen);
    const meta = BLOCK_META[blockKey];
    if (!meta) return null;
    const Icon = meta.icon;
    const suggestions = block?.suggestions || [];
    const skipped = !!block?.skipped;
    const priority = block?.priority || "low";
    const impact = block?.impact || "—";

    return (
        <section
            data-testid={`v50-block-${blockKey}`}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid={`v50-block-toggle-${blockKey}`}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-[var(--color-panel-ai)]/40 transition-colors"
            >
                <div className="flex items-start gap-4 min-w-0">
                    <div className="h-10 w-10 rounded-xl bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/30 grid place-items-center shrink-0">
                        <Icon className="h-5 w-5 text-[var(--color-primary)]" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-base md:text-lg font-semibold text-text-main truncate">
                            {meta.title}
                        </h3>
                        <p className="text-xs text-text-secondary mt-0.5">{meta.subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {skipped ? (
                        <span className="text-[10px] uppercase tracking-widest text-text-secondary">skipped</span>
                    ) : (
                        <>
                            <PriorityChip level={priority} testid={`v50-block-prio-${blockKey}`} />
                            <span className="text-xs text-text-secondary tabular-nums">
                                {suggestions.length} suggestions
                            </span>
                        </>
                    )}
                    {open ? <ChevronDown className="h-4 w-4 text-text-secondary" /> : <ChevronRight className="h-4 w-4 text-text-secondary" />}
                </div>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-[var(--color-border)] p-5 md:p-6 space-y-3">
                            {skipped ? (
                                <p className="text-xs text-text-secondary italic">
                                    Engine skipped — {block?.reason || "disabled via feature flag"}.
                                </p>
                            ) : (
                                <>
                                    {block?.reasoning && (
                                        <p className="text-xs text-text-secondary italic">
                                            {block.reasoning}
                                        </p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary">
                                        <span>impact · {impact}</span>
                                        {block?.risk && <span>· risk · {block.risk}</span>}
                                        {block?.duration_ms != null && (
                                            <span>· {block.duration_ms}ms</span>
                                        )}
                                    </div>
                                    {suggestions.length === 0 ? (
                                        <p className="text-xs text-text-secondary">No suggestions from this engine.</p>
                                    ) : (
                                        <ul className="space-y-2">
                                            {suggestions.map((s) => (
                                                <SuggestionRow
                                                    key={s.id}
                                                    suggestion={s}
                                                    reportId={reportId}
                                                    onChanged={onChanged}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}


export default function AdminV50() {
    const { user: me } = useAuth();
    const [reports, setReports] = useState([]);
    const [currentId, setCurrentId] = useState(null);
    const [current, setCurrent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    // Phase 16a — Weekly digest
    const [digests, setDigests] = useState([]);
    const [digestCronOn, setDigestCronOn] = useState(false);
    const [digestBusy, setDigestBusy] = useState(false);

    const loadDigests = useCallback(async () => {
        try {
            const r = await api.v50DigestList(10);
            setDigests(r?.items || []);
        } catch (e) {
            if (e.status !== 503) {
                // 503 means V50 master flag off — silent, main dashboard handles it
                console.warn("digest list failed", e);
            }
        }
        try {
            const settings = await apiFetch("/admin/system-settings");
            setDigestCronOn(!!(settings.toggles || {}).V50_DIGEST_CRON_ENABLED);
        } catch {
            /* ignore */
        }
    }, []);

    const generateDigestNow = async () => {
        setDigestBusy(true);
        try {
            const r = await api.v50DigestGenerateNow();
            if (r.already_generated) {
                toast.info(`Digest already generated for this week · suggestion ${r.suggestion_id.slice(0, 8)}`);
            } else {
                toast.success(`Digest generated · suggestion ${r.suggestion_id.slice(0, 8)}`);
            }
            await loadDigests();
        } catch (e) {
            toast.error(e.message || "Digest generation failed");
        } finally {
            setDigestBusy(false);
        }
    };

    const loadReports = useCallback(async () => {
        try {
            const r = await apiFetch("/v50/reports?limit=20");
            setReports(r.items || []);
            if (!currentId && (r.items || []).length > 0) {
                setCurrentId(r.items[0].id);
            }
        } catch (e) {
            if (e.status === 503) {
                toast.error("V50 is disabled — flip V50_SELF_OPT_ENABLED in Admin → System");
            } else {
                toast.error(e.message || "Failed to list V50 reports");
            }
        }
    }, [currentId]);

    const loadCurrent = useCallback(async (id) => {
        if (!id) { setCurrent(null); return; }
        try {
            const r = await apiFetch(`/v50/reports/${encodeURIComponent(id)}`);
            setCurrent(r);
        } catch (e) {
            toast.error(e.message || "Failed to load V50 report");
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await loadReports();
            await loadDigests();
            setLoading(false);
        })();
    }, [loadReports, loadDigests]);

    useEffect(() => { loadCurrent(currentId); }, [currentId, loadCurrent]);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await apiFetch("/v50/run", { method: "POST", body: JSON.stringify({}) });
            toast.success(`V50 run complete · ${r?.totals?.suggestions_persisted || 0} suggestions`);
            setCurrentId(r.id);
            await loadReports();
        } catch (e) {
            if (e.status === 429) {
                toast.error("Daily V50 run quota reached (5/day). Try again tomorrow.");
            } else if (e.status === 503) {
                toast.error("V50 is disabled. Flip V50_SELF_OPT_ENABLED to run it.");
            } else {
                toast.error(e.message || "V50 run failed");
            }
        } finally {
            setRunning(false);
        }
    };

    const totals = current?.totals || {};
    const errors = current?.errors || {};
    const errorCount = Object.keys(errors).length;
    const counts = current?.suggestion_status_counts || {};

    const kpis = useMemo(() => ({
        suggestions: totals.suggestions_persisted || 0,
        pending: counts.pending_review || 0,
        acknowledged: counts.acknowledged || 0,
        dismissed: counts.dismissed || 0,
        errors: errorCount,
    }), [totals, counts, errorCount]);

    if (!isAdminRole(me?.role)) {
        return (
            <div
                data-testid="admin-v50-forbidden"
                className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"
            >
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                    data-testid="admin-v50"
                >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                V50 · Self-Optimization
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main">
                                Meta-intelligence report
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                Aggregates 7 sub-engines into a unified 5-block strategic view.
                                Human-in-the-loop: every suggestion goes into the autonomy queue.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a
                                href="/admin/governance"
                                data-testid="v50-back-governance"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1"
                            >
                                Governance <ArrowUpRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={() => { loadReports(); loadCurrent(currentId); }}
                                data-testid="v50-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                            <button
                                onClick={runNow}
                                disabled={running}
                                data-testid="v50-run-now"
                                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50"
                            >
                                {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                                Run V50 now
                            </button>
                        </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 mb-6">
                        <KpiCard icon={Sparkles} label="Suggestions" value={kpis.suggestions}
                                  tone="primary" testid="v50-kpi-total" />
                        <KpiCard icon={Clock} label="Pending review" value={kpis.pending}
                                  tone="warning" testid="v50-kpi-pending" />
                        <KpiCard icon={CheckCircle2} label="Acknowledged" value={kpis.acknowledged}
                                  tone="success" testid="v50-kpi-ack" />
                        <KpiCard icon={Ban} label="Dismissed" value={kpis.dismissed}
                                  tone="neutral" testid="v50-kpi-dismissed" />
                        <KpiCard icon={ShieldAlert} label="Engine errors" value={kpis.errors}
                                  tone={kpis.errors > 0 ? "alert" : "neutral"} testid="v50-kpi-errors" />
                    </div>

                    {/* Phase 16a — Weekly Digest card */}
                    <section
                        data-testid="v50-digest-card"
                        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 mb-6"
                    >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <Send className="h-4 w-4 text-[var(--color-secondary)]" />
                                    <span className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] font-semibold">
                                        Phase 16a · Weekly Digest
                                    </span>
                                    {digestCronOn ? (
                                        <span
                                            data-testid="v50-digest-cron-on"
                                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] bg-[var(--color-success)]/10"
                                        >
                                            Cron ON · Mon 09:00 UTC
                                        </span>
                                    ) : (
                                        <span
                                            data-testid="v50-digest-cron-off"
                                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--color-border)] text-text-secondary"
                                        >
                                            Cron OFF
                                        </span>
                                    )}
                                </div>
                                <h3 className="text-lg font-semibold text-text-main">
                                    Founder-only V50 digest
                                </h3>
                                <p className="text-xs text-text-secondary mt-1">
                                    Aggregates SEO coverage, new content, top diagnostics + growth opportunities.
                                    Generates a <code className="text-text-main">v50_weekly_digest</code> suggestion —
                                    you acknowledge + dispatch it from <a href="/admin/email-send" className="text-[var(--color-primary)] underline">/admin/email-send</a>.
                                </p>
                                {digests.length > 0 ? (
                                    <div className="mt-3 flex items-center gap-3 text-[11px] text-text-secondary flex-wrap">
                                        <span>Latest:</span>
                                        <span className="font-mono text-text-main">
                                            {(digests[0].generated_at || "").slice(0, 16).replace("T", " ")} UTC
                                        </span>
                                        <span>·</span>
                                        <span data-testid="v50-digest-latest-status" className={digests[0].dispatched
                                            ? "text-[var(--color-success)]"
                                            : "text-neutral-400"}>
                                            {digests[0].dispatched ? "dispatched" : "pending dispatch"}
                                        </span>
                                        {digests[0].coverage_alert_triggered && (
                                            <>
                                                <span>·</span>
                                                <span className="text-[var(--color-alert)] flex items-center gap-1">
                                                    <AlertTriangle className="h-3 w-3" /> coverage alert
                                                </span>
                                            </>
                                        )}
                                        <span>·</span>
                                        <span className="font-mono">{digests[0].suggestion_id?.slice(0, 8)}</span>
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-text-secondary italic mt-3">
                                        No digest generated yet.
                                    </p>
                                )}
                                {digests.length > 0 && !digests[0].dispatched && (
                                    <div
                                        data-testid="v50-digest-pending-cta"
                                        className="mt-3 rounded-lg border border-neutral-500/30 bg-neutral-500/10 px-3 py-2 text-[11px] text-neutral-300/90 flex items-center gap-2"
                                    >
                                        <Mail className="h-3 w-3 shrink-0" />
                                        <span>1 digest ready to send — </span>
                                        <a href="/admin/email-send" className="text-[var(--color-secondary)] underline">
                                            open Email Send
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                <button
                                    onClick={generateDigestNow}
                                    disabled={digestBusy}
                                    data-testid="v50-digest-generate-now"
                                    className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50"
                                >
                                    {digestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Rocket className="h-3 w-3" />}
                                    Generate now
                                </button>
                                <a
                                    href="/admin/email-send"
                                    className="text-[11px] text-text-secondary hover:text-text-main text-center px-3 py-1"
                                >
                                    Open Email Send →
                                </a>
                            </div>
                        </div>
                    </section>

                    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
                        <aside className="lg:sticky lg:top-6 lg:self-start">
                            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                                <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                                    Report history
                                </div>
                                {loading ? (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                                    </div>
                                ) : reports.length === 0 ? (
                                    <p className="text-xs text-text-secondary">
                                        No V50 run yet. Click <span className="text-text-main">Run V50 now</span>.
                                    </p>
                                ) : (
                                    <ul className="space-y-1 max-h-96 overflow-y-auto pr-1">
                                        {reports.map((r) => (
                                            <li key={r.id}>
                                                <button
                                                    onClick={() => setCurrentId(r.id)}
                                                    data-testid={`v50-report-item-${r.id}`}
                                                    className={`w-full text-left rounded-lg px-3 py-2 text-xs transition-colors ${
                                                        r.id === currentId
                                                            ? "bg-[var(--color-panel-ai)] text-text-main"
                                                            : "text-text-secondary hover:bg-[var(--color-panel-ai)]/60 hover:text-text-main"
                                                    }`}
                                                >
                                                    <div className="tabular-nums">{(r.generated_at || "").slice(0, 16).replace("T", " ")}</div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] uppercase tracking-widest">{r.triggered_by || "manual"}</span>
                                                        <span className="text-[10px] text-[var(--color-primary)]">
                                                            · {r.totals?.suggestions_persisted || 0}
                                                        </span>
                                                        {r.status !== "completed" && (
                                                            <span className="text-[10px] text-[var(--color-alert)]">· {r.status}</span>
                                                        )}
                                                    </div>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </aside>

                        <div className="space-y-4">
                            {!current ? (
                                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center">
                                    <Gauge className="h-8 w-8 text-[var(--color-primary)] mx-auto mb-3" />
                                    <p className="text-sm text-text-secondary">
                                        Run V50 to generate your first meta-intelligence report.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                                        <div className="flex flex-wrap items-center gap-4 text-xs text-text-secondary">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {(current.generated_at || "").slice(0, 19).replace("T", " ")} UTC
                                            </span>
                                            <span>· duration {current.duration_ms}ms</span>
                                            <span>· model <code className="text-text-main">{current.model_used}</code></span>
                                            <span>· triggered by <span className="text-text-main">{current.triggered_by}</span></span>
                                            <span>· top priority <PriorityChip level={totals.top_priority} testid="v50-top-priority" /></span>
                                        </div>
                                        {errorCount > 0 && (
                                            <div className="mt-3 rounded-lg border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 px-3 py-2 text-xs text-[var(--color-alert)] flex items-start gap-2">
                                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="font-medium">Partial run — {errorCount} engine(s) failed</div>
                                                    <div className="opacity-80 truncate">
                                                        {Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {["diagnostics", "improvement", "expansion",
                                        "marketing", "analytics", "scaling", "evolution"].map((k, idx) => (
                                        <BlockShell
                                            key={k}
                                            blockKey={k}
                                            block={current.blocks?.[k]}
                                            reportId={current.id}
                                            defaultOpen={idx === 0}
                                            onChanged={() => loadCurrent(current.id)}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
